import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Loader2, X } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

export default function Tasks() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [status, setStatus] = useState("open");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = status === "all" ? {} : { status_filter: status };
    return api.get("/tasks", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/staff/list").then((r) => setStaff(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const upd = async (id, patch) => {
    try { await api.patch(`/tasks/${id}`, patch); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete task?")) return;
    try { await api.delete(`/tasks/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fade-in" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">CRM</div>
          <h2 className="font-serif text-3xl text-njas-navy">Tasks</h2>
        </div>
        <div className="flex gap-2 items-center">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3" data-testid="tasks-status-filter">
            <option value="all">All</option><option value="open">Open</option><option value="in_progress">In progress</option><option value="done">Done</option><option value="cancelled">Cancelled</option>
          </select>
          <button onClick={() => setShowNew(true)} data-testid="new-task-global" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2.5 text-sm hover:bg-njas-navy-hover">
            <Plus className="w-4 h-4" /> New task
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {loading && <div className="text-njas-muted text-sm">Loading…</div>}
        {!loading && rows.length === 0 && <div className="text-njas-muted text-sm">No tasks.</div>}
        {rows.map((t) => (
          <div key={t.id} className="rounded-lg bg-white border border-[var(--njas-border)] p-4 flex items-center gap-3" data-testid={`task-${t.id}`}>
            <input type="checkbox" checked={t.status === "done"} onChange={(e) => upd(t.id, { status: e.target.checked ? "done" : "open" })} />
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${t.status === "done" ? "text-njas-muted line-through" : "text-njas-navy"}`}>{t.title}</div>
              <div className="text-xs text-njas-muted">
                {t.priority !== "normal" && <span className="capitalize mr-2 text-orange-600">{t.priority}</span>}
                {t.due_date && <span>Due {new Date(t.due_date).toLocaleDateString("en-AU")} · </span>}
                {t.assignee_name || "Unassigned"}
                {t.client_id && <> · <Link to={`/dashboard/clients/${t.client_id}`} className="text-njas-navy hover:underline">{t.client_name}</Link></>}
              </div>
              {t.description && <div className="text-xs text-njas-muted mt-1 whitespace-pre-wrap">{t.description}</div>}
            </div>
            <button onClick={() => setEditing(t)} className="text-xs text-njas-navy hover:underline">Edit</button>
            <button onClick={() => del(t.id)} className="text-xs text-red-600 hover:underline">Delete</button>
          </div>
        ))}
      </div>

      {showNew && <TaskModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} staff={staff} clients={clients} />}
      {editing && <TaskModal onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} staff={staff} clients={clients} initial={editing} />}
    </div>
  );
}

function TaskModal({ onClose, onSaved, staff, clients, initial }) {
  const [f, set] = useState({
    title: initial?.title || "", description: initial?.description || "",
    due_date: initial?.due_date || "", priority: initial?.priority || "normal",
    assignee_id: initial?.assignee_id || "", client_id: initial?.client_id || "", status: initial?.status || "open",
    duration_minutes: initial?.duration_minutes || "",
    funding_source: initial?.funding_source || "",
    funding_details: initial?.funding_details || "",
    activity_date: initial?.activity_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...f,
        due_date: f.due_date || null, assignee_id: f.assignee_id || null, client_id: f.client_id || null,
        duration_minutes: f.duration_minutes === "" ? null : Number(f.duration_minutes),
        funding_source: f.funding_source || null,
        funding_details: f.funding_details || null,
        activity_date: f.activity_date || null,
      };
      if (initial?.id) await api.patch(`/tasks/${initial.id}`, body);
      else await api.post("/tasks", body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif text-xl text-njas-navy">{initial?.id ? "Edit task" : "New task"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <form onSubmit={save} className="space-y-3" data-testid="task-form">
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Title</span><input required value={f.title} onChange={upd("title")} className={inputCls} /></label>
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Description / what was done</span><textarea rows={2} value={f.description} onChange={upd("description")} className={inputCls} /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Activity date</span><input type="date" value={f.activity_date} onChange={upd("activity_date")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Duration (mins)</span><input type="number" min="0" value={f.duration_minutes} onChange={upd("duration_minutes")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Funding source</span>
              <select value={f.funding_source} onChange={upd("funding_source")} className={inputCls}>
                <option value="">—</option><option value="medicare">Medicare</option><option value="ndis">NDIS</option>
                <option value="grant">Grant</option><option value="private">Private</option><option value="pro_bono">Pro bono</option><option value="other">Other</option>
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Funding details</span><input value={f.funding_details} onChange={upd("funding_details")} placeholder="Grant name / program" className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Due date</span><input type="date" value={f.due_date} onChange={upd("due_date")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Priority</span>
              <select value={f.priority} onChange={upd("priority")} className={inputCls}>
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Assignee</span>
              <select value={f.assignee_id} onChange={upd("assignee_id")} className={inputCls}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Linked client (optional)</span>
              <select value={f.client_id} onChange={upd("client_id")} className={inputCls}>
                <option value="">—</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </label>
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Status</span>
              <select value={f.status} onChange={upd("status")} className={inputCls}>
                <option value="open">Open</option><option value="in_progress">In progress</option><option value="done">Done</option><option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
